import { useState, useEffect } from "react";
import { getMonthlyStats, getOperatorStats, getFlightTypeStats, getAircraftTypeStats, getRoutesStats, getServiceStats, getApronUsageStats, getAircraftTypeApronStats, getContinentStats, getProximityStats, getGeoStats, getCargoStats, getDelayStats, getFinancialStats } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, LabelList, ReferenceLine } from "recharts";
import { Loader2, TrendingUp, PlaneTakeoff, Building2, Route, Fuel, MapPin, Plane, Globe, Radar, Package, Scale, Clock, Euro } from "lucide-react";
import { MapContainer, TileLayer, Circle, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import { useTheme } from "@/context/ThemeContext";
import { useAvailableYears } from "@/hooks/useAvailableYears";
import "leaflet/dist/leaflet.css";

const COLORS = {
  navy: "#0f172a",
  gold: "#d4af37",
  emerald: "#10b981",
  amber: "#f59e0b",
  slate: "#64748b",
  red: "#ef4444",
  blue: "#3b82f6",
  purple: "#8b5cf6"
};

const PIE_COLORS = [COLORS.navy, COLORS.gold, COLORS.emerald, COLORS.amber, COLORS.slate, COLORS.red, COLORS.blue, COLORS.purple];

const EBCI_CENTER = [50.459, 4.453];

const CONTINENT_COLORS = {
  "Europe (Schengen)": "#60a5fa",
  "Europe (Non-Schengen)": "#f87171",
  "North America": "#22c55e",
  "South America": "#facc15",
  "Asia": "#a855f7",
  "Africa": "#ea580c",
  "Oceania": "#ffffff",
  "Other": "#000000",
};

// Custom pie label factory (theme-aware): only render for slices >= 5%
const makePieLabel = (fill) => ({ cx, cy, midAngle, outerRadius, percent, name }) => {
  if (percent < 0.05) return null;
  const RAD = Math.PI / 180;
  const r = outerRadius + 22;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text x={x} y={y} fill={fill} fontSize={12} textAnchor={x > cx ? "start" : "end"} dominantBaseline="central">
      {`${name} ${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export default function StatisticsPage() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const axisColor = isDark ? "#94a3b8" : "#64748b";
  const gridColor = isDark ? "#334155" : "#e2e8f0";
  const labelColor = isDark ? "#e2e8f0" : "#334155";
  const chartNavy = isDark ? "#60a5fa" : "#0f172a";
  const tooltipStyle = {
    backgroundColor: isDark ? "#1e293b" : "#ffffff",
    border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
    borderRadius: 8,
    color: isDark ? "#e2e8f0" : "#0f172a",
  };
  const pieColors = [chartNavy, COLORS.gold, COLORS.emerald, COLORS.amber, COLORS.slate, COLORS.red, COLORS.blue, COLORS.purple];
  const continentColors = CONTINENT_COLORS;
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [flightStatus, setFlightStatus] = useState("all");
  const [canceledFilter, setCanceledFilter] = useState("exclude");
  const [monthlyData, setMonthlyData] = useState([]);
  const [operatorData, setOperatorData] = useState([]);
  const [flightTypeData, setFlightTypeData] = useState([]);
  const [aircraftData, setAircraftData] = useState([]);
  const [routesData, setRoutesData] = useState([]);
  const [serviceData, setServiceData] = useState([]);
  const [apronUsageData, setApronUsageData] = useState({ by_position: [], by_apron: [], total: 0 });
  const [aircraftApronData, setAircraftApronData] = useState([]);
  const [continentData, setContinentData] = useState([]);
  const [proximityData, setProximityData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [cargoData, setCargoData] = useState(null);
  const [delayData, setDelayData] = useState(null);
  const [proximityRadius, setProximityRadius] = useState(500);
  const [radiusInput, setRadiusInput] = useState("500");
  const [finStats, setFinStats] = useState(null);

  useEffect(() => {
    getFinancialStats().then((r) => setFinStats(r.data.statistics)).catch(() => setFinStats(null));
  }, []);

  useEffect(() => {
    fetchAllStats();
  }, [selectedYear, selectedMonth, flightStatus, canceledFilter]);

  const fetchAllStats = async () => {
    setLoading(true);
    const yearNum = selectedYear === "all" ? undefined : parseInt(selectedYear);
    const filters = {
      ...(yearNum ? { year: yearNum } : {}),
      ...(selectedMonth !== "all" ? { month: parseInt(selectedMonth) } : {}),
      flight_status: flightStatus,
      canceled: canceledFilter,
    };
    try {
      const [monthly, operators, flightTypes, aircraft, routes, services, apronUsage, aircraftApron, continents, proximity, geo, cargo, delays] = await Promise.all([
        getMonthlyStats(yearNum, { flight_status: flightStatus, canceled: canceledFilter }),
        getOperatorStats(filters),
        getFlightTypeStats(filters),
        getAircraftTypeStats(filters),
        getRoutesStats(filters),
        getServiceStats(filters),
        getApronUsageStats(filters),
        getAircraftTypeApronStats(filters),
        getContinentStats(filters),
        getProximityStats(proximityRadius, filters),
        getGeoStats(filters),
        getCargoStats(filters),
        getDelayStats(filters)
      ]);
      
      setMonthlyData(monthly.data);
      setOperatorData(operators.data);
      setFlightTypeData(flightTypes.data);
      setAircraftData(aircraft.data);
      setRoutesData(routes.data);
      setServiceData(services.data);
      setApronUsageData(apronUsage.data);
      setAircraftApronData(aircraftApron.data);
      setContinentData(continents.data);
      setProximityData(proximity.data);
      setGeoData(geo.data);
      setCargoData(cargo.data);
      setDelayData(delays.data);
    } catch (error) {
      console.error("Failed to fetch statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyRadius = async () => {
    const r = Math.max(50, Math.min(20000, parseInt(radiusInput) || 500));
    setProximityRadius(r);
    setRadiusInput(r.toString());
    try {
      const filters = {
        ...(selectedYear === "all" ? {} : { year: parseInt(selectedYear) }),
        ...(selectedMonth !== "all" ? { month: parseInt(selectedMonth) } : {}),
        flight_status: flightStatus,
        canceled: canceledFilter,
      };
      const res = await getProximityStats(r, filters);
      setProximityData(res.data);
    } catch (error) {
      console.error("Failed to fetch proximity:", error);
    }
  };

  const years = useAvailableYears();
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const STATUS_LABELS = { all: "All flights", actual: "Departed / Landed", booked: "Booked flights" };
  const CANCELED_LABELS = { exclude: "No canceled", include: "Incl. canceled", only: "Only canceled" };
  const FIN_MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
  const eurFmt = (v) => new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
  const finLatest = finStats?.years?.[finStats.years.length - 1];
  const finBest = finStats?.years?.reduce((a, b) => (b.total > (a?.total || 0) ? b : a), null);
  const curYearNum = new Date().getFullYear();
  const finCurrent = finStats?.years?.find((s) => s.year === curYearNum);
  const finChartData = (finStats?.years || []).map((s) => ({
    year: String(s.year),
    actual: s.total,
    foreseen: Math.max(0, (s.projected_total ?? s.total) - s.total),
  }));
  const finPaceDiff = finCurrent && finCurrent.yoy_ytd_pct != null ? finCurrent.yoy_ytd_pct - (finCurrent.growth_target_pct ?? 6) : null;
  const onTrack = finPaceDiff != null && finPaceDiff >= 0;
  const finGoal = finStats?.years?.find((y) => y.growth_target_pct != null)?.growth_target_pct ?? 6;
  const renderYearTotalLabel = (props) => {
    const { x = 0, y = 0, width = 0, index } = props;
    const d = finChartData[index];
    if (!d) return null;
    return (
      <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill={labelColor}>
        {eurFmt(d.actual + d.foreseen)}
      </text>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  const monthIdx = selectedMonth !== "all" ? parseInt(selectedMonth) - 1 : -1;
  const totalFlights = monthIdx >= 0
    ? (monthlyData[monthIdx]?.flights || 0)
    : monthlyData.reduce((sum, m) => sum + m.flights, 0);
  const totalPassengers = monthIdx >= 0
    ? (monthlyData[monthIdx]?.passengers || 0)
    : monthlyData.reduce((sum, m) => sum + m.passengers, 0);
  const periodLabel = `${selectedMonth !== "all" ? MONTHS[monthIdx] + " " : ""}${selectedYear === "all" ? "All Years" : selectedYear} · ${STATUS_LABELS[flightStatus]}${canceledFilter !== "exclude" ? " · " + CANCELED_LABELS[canceledFilter] : ""}`;

  // Augment a dataset with a "count (pct%)" label relative to its own total.
  const withPct = (arr, key) => {
    const total = arr.reduce((s, d) => s + (d[key] || 0), 0) || 1;
    return arr.map((d) => {
      const pct = (d[key] / total) * 100;
      return { ...d, _pct: pct, _label: `${d[key]} (${pct.toFixed(pct < 1 && pct > 0 ? 1 : 0)}%)` };
    });
  };
  const aircraftPct = withPct(aircraftData, "count");
  const servicePct = withPct(serviceData, "count").filter((s) => s.count > 0);
  const aircraftApronPct = withPct(aircraftApronData, "count");
  const continentPct = withPct(continentData, "count");
  const positionPct = withPct(apronUsageData.by_position, "count");
  const operatorTotal = operatorData.reduce((s, o) => s + (o.flights || 0), 0) || 1;
  const routeTotal = routesData.reduce((s, r) => s + (r.count || 0), 0) || 1;
  const flightTypeTotal = flightTypeData.reduce((s, d) => s + (d.count || 0), 0) || 1;

  return (
    <div className="animate-fade-in" data-testid="statistics-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a] font-['Outfit']">Statistics</h1>
          <p className="text-[#64748b] mt-1">Analytics and insights from flight data</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={canceledFilter} onValueChange={setCanceledFilter}>
            <SelectTrigger className="w-40" data-testid="canceled-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="exclude">No canceled</SelectItem>
              <SelectItem value="include">Incl. canceled</SelectItem>
              <SelectItem value="only">Only canceled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={flightStatus} onValueChange={setFlightStatus}>
            <SelectTrigger className="w-44" data-testid="flight-status-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All flights</SelectItem>
              <SelectItem value="actual">Departed / Landed</SelectItem>
              <SelectItem value="booked">Booked flights</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-36" data-testid="month-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All months</SelectItem>
              {MONTHS.map((m, i) => (
                <SelectItem key={m} value={(i + 1).toString()}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-28" data-testid="year-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {years.map((year) => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card className="border-[#e2e8f0]">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-14 h-14 bg-[#0f172a] rounded-xl flex items-center justify-center">
              <TrendingUp size={28} className="text-[#d4af37]" />
            </div>
            <div>
              <p className="text-sm text-[#64748b]">Total Flights</p>
              <p className="text-3xl font-bold text-[#0f172a] font-['Outfit']" data-testid="summary-total-flights">{totalFlights.toLocaleString()}</p>
              <p className="text-xs text-[#94a3b8] mt-0.5">{periodLabel}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#e2e8f0]">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-14 h-14 bg-[#d4af37] rounded-xl flex items-center justify-center">
              <PlaneTakeoff size={28} className="text-[#0f172a]" />
            </div>
            <div>
              <p className="text-sm text-[#64748b]">Total Passengers</p>
              <p className="text-3xl font-bold text-[#0f172a] font-['Outfit']" data-testid="summary-total-pax">{totalPassengers.toLocaleString()}</p>
              <p className="text-xs text-[#94a3b8] mt-0.5">{periodLabel}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Flights Chart */}
      <Card className="border-[#e2e8f0] mb-8">
        <CardHeader>
          <CardTitle className="font-['Outfit'] text-[#0f172a]">Monthly Flights & Passengers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="month_name" tick={{ fill: axisColor, fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fill: axisColor, fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: axisColor, fontSize: 12 }} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelStyle={{ color: labelColor }}
                  itemStyle={{ color: labelColor }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="flights" name="Flights" fill={chartNavy} radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="passengers" name="Passengers" fill={COLORS.gold} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Yearly Income (visible to all with statistics access) */}
      {finStats && finStats.years.length > 0 && (
        <Card className="border-[#e2e8f0] mb-8" data-testid="yearly-income-card">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <Euro size={20} className="text-[#d4af37]" /> Yearly Income
            </CardTitle>
            <p className="text-sm text-[#64748b] mt-1">
              Total income per year. The lighter segment on {curYearNum} is the <strong>foreseen</strong> income still needed to beat {curYearNum - 1} by at least {finGoal}%.
            </p>
          </CardHeader>
          <CardContent>
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={finChartData} margin={{ top: 24, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                  <XAxis dataKey="year" tick={{ fill: axisColor, fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fill: axisColor, fontSize: 12 }} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelStyle={{ color: labelColor }}
                    itemStyle={{ color: labelColor }}
                    formatter={(v, n) => [eurFmt(v), n === "foreseen" ? `Foreseen (${finGoal}% target)` : "Actual"]}
                  />
                  <Legend formatter={(v) => (v === "foreseen" ? `Foreseen (${finGoal}% target)` : "Actual")} />
                  <Bar dataKey="actual" name="actual" stackId="inc" radius={[4, 4, 0, 0]}>
                    {finChartData.map((d, i) => (
                      <Cell key={i} fill={finBest && String(finBest.year) === d.year ? COLORS.gold : chartNavy} />
                    ))}
                  </Bar>
                  <Bar dataKey="foreseen" name="foreseen" stackId="inc" fill={COLORS.emerald} fillOpacity={0.45} radius={[4, 4, 0, 0]}>
                    <LabelList content={renderYearTotalLabel} />
                  </Bar>
                  {finCurrent && finCurrent.prev_total && (
                    <ReferenceLine y={finCurrent.prev_total} stroke="#64748b" strokeDasharray="5 4" strokeWidth={1.5} ifOverflow="extendDomain"
                      label={{ value: `Break-even ${curYearNum - 1}`, position: "insideTopLeft", fill: "#64748b", fontSize: 10, fontWeight: 600 }} />
                  )}
                  {finCurrent && finCurrent.forecasts?.plus6?.target_total && (
                    <ReferenceLine y={finCurrent.forecasts.plus6.target_total} stroke={COLORS.emerald} strokeDasharray="5 4" strokeWidth={1.5} ifOverflow="extendDomain"
                      label={{ value: `+${finGoal}% target`, position: "insideBottomRight", fill: COLORS.emerald, fontSize: 10, fontWeight: 600 }} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>

            {finCurrent && finCurrent.forecasts && finCurrent.open_months && finCurrent.open_months.length > 0 && (
              <div className="mt-6 space-y-4" data-testid="forecast-panel">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-[#0f172a] dark:text-gray-100">
                    Targets for the open months of {curYearNum} ({finCurrent.open_months.map((m) => MONTHS[FIN_MONTHS.indexOf(m)]).join(", ")})
                  </p>
                  {finCurrent.yoy_ytd_pct != null && (
                    <span data-testid="pace-badge" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${onTrack ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"}`}>
                      <span className={`h-2 w-2 rounded-full ${onTrack ? "bg-emerald-500" : "bg-amber-500"}`} />
                      {onTrack ? "On track" : "Behind pace"} · finished months {finCurrent.yoy_ytd_pct > 0 ? "+" : ""}{finCurrent.yoy_ytd_pct}% vs +{finCurrent.growth_target_pct}% goal
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
                    <p className="text-sm text-[#64748b]">Break-even — match {curYearNum - 1} ({eurFmt(finCurrent.prev_total)})</p>
                    <p className="text-xl font-bold text-[#0f172a] dark:text-white font-['Outfit']"><span className="text-sm font-medium text-[#64748b]">still need </span><span data-testid="forecast-breakeven-total">{eurFmt(finCurrent.forecasts.breakeven.forecast_total)}</span></p>
                    <p className="text-xs text-[#64748b] mt-0.5">projected {eurFmt(finCurrent.forecasts.breakeven.projected_total)}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/40">
                    <p className="text-sm text-emerald-700 dark:text-emerald-400 font-semibold">Beat {curYearNum - 1} by +{finGoal}% ({eurFmt(finCurrent.forecasts.plus6.target_total)})</p>
                    <p className="text-xl font-bold text-emerald-700 dark:text-emerald-400 font-['Outfit']"><span className="text-sm font-medium">still need </span><span data-testid="forecast-remaining-total">{eurFmt(finCurrent.forecasts.plus6.forecast_total)}</span></p>
                    <p className="text-xs text-emerald-700/80 dark:text-emerald-400/80 mt-0.5">projected {eurFmt(finCurrent.forecasts.plus6.projected_total)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-[#64748b] mb-2">Minimum each open month needs for the <strong>+{finGoal}%</strong> goal:</p>
                  <div className="flex flex-wrap gap-2">
                    {FIN_MONTHS.map((m, i) =>
                      finCurrent.forecasts.plus6.forecast_months[m] != null ? (
                        <span key={m} className="px-2.5 py-1 rounded-lg bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-900/50 text-xs" data-testid={`forecast-${m}`}>
                          <span className="font-semibold text-[#0f172a] dark:text-gray-200">{MONTHS[i]}</span>
                          <span className="text-emerald-700 dark:text-emerald-400 ml-1.5 font-bold tabular-nums">≥ {eurFmt(finCurrent.forecasts.plus6.forecast_months[m])}</span>
                        </span>
                      ) : null
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Financial Income (Year-over-Year) */}
      {finStats && finStats.years.length > 0 && (
        <Card className="border-[#e2e8f0] mb-8" data-testid="financial-income-card">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <Euro size={20} className="text-[#d4af37]" /> Financial Income (Year-over-Year)
            </CardTitle>
            <p className="text-sm text-[#64748b] mt-1">Like-for-like: each year is compared with the <strong>same months</strong> of the previous year, so incomplete years are never over-stated.</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40">
                <p className="text-sm text-[#64748b]">{finLatest.year} income</p>
                <p className="text-2xl font-bold text-[#0f172a] dark:text-white font-['Outfit']" data-testid="fin-latest-total">{eurFmt(finLatest.total)}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40">
                <p className="text-sm text-[#64748b]">vs previous year (same months)</p>
                <p className={`text-2xl font-bold font-['Outfit'] ${finLatest.yoy_ytd_pct == null ? "text-[#0f172a] dark:text-white" : finLatest.yoy_ytd_pct >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="fin-yoy-ytd">
                  {finLatest.yoy_ytd_pct == null ? "—" : `${finLatest.yoy_ytd_pct > 0 ? "+" : ""}${finLatest.yoy_ytd_pct}%`}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40">
                <p className="text-sm text-[#64748b]">Best year</p>
                <p className="text-2xl font-bold text-[#0f172a] dark:text-white font-['Outfit']">{finBest ? `${finBest.year} · ${eurFmt(finBest.total)}` : "—"}</p>
              </div>
            </div>
            <p className="text-sm font-semibold text-[#0f172a] dark:text-gray-200 mb-2">Monthly change vs previous year</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse min-w-[900px]" data-testid="fin-yoy-table">
                <thead>
                  <tr className="text-[#64748b] border-b border-[#e2e8f0]">
                    <th className="px-3 py-2 text-left">Year</th>
                    {MONTHS.map((m) => <th key={m} className="px-2 py-2 text-right">{m}</th>)}
                    <th className="px-3 py-2 text-right">Same-months</th>
                  </tr>
                </thead>
                <tbody>
                  {[...finStats.years].reverse().filter((s) => s.yoy_ytd_pct != null || Object.values(s.month_yoy_pct).some((v) => v != null)).map((s) => (
                    <tr key={s.year} className="border-b border-[#f1f5f9]">
                      <td className="px-3 py-1.5 font-bold text-[#0f172a] dark:text-gray-200">{s.year}</td>
                      {FIN_MONTHS.map((m) => {
                        const isOpen = (s.open_months || []).includes(m);
                        const p = s.month_yoy_pct[m];
                        return <td key={m} className={`px-2 py-1.5 text-right tabular-nums font-medium ${isOpen ? "text-amber-500" : p == null ? "text-slate-300" : p >= 0 ? "text-emerald-600" : "text-red-600"}`}>{isOpen ? <span className="italic text-xs font-semibold">open</span> : p == null ? "—" : `${p > 0 ? "+" : ""}${p}%`}</td>;
                      })}
                      <td className={`px-3 py-1.5 text-right tabular-nums font-bold ${s.yoy_ytd_pct == null ? "text-slate-300" : s.yoy_ytd_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {s.yoy_ytd_pct == null ? "—" : `${s.yoy_ytd_pct > 0 ? "+" : ""}${s.yoy_ytd_pct}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm font-semibold text-[#0f172a] dark:text-gray-200 mb-2 mt-6">Quarterly change vs previous year</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse min-w-[520px]" data-testid="fin-quarter-table">
                <thead>
                  <tr className="text-[#64748b] border-b border-[#e2e8f0]">
                    <th className="px-3 py-2 text-left">Year</th>
                    {[["q1", "Q1"], ["q2", "Q2"], ["q3", "Q3"], ["q4", "Q4"]].map(([k, l]) => <th key={k} className="px-2 py-2 text-right">{l}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[...finStats.years].reverse().filter((s) => s.quarters && Object.values(s.quarters).some((q) => q.yoy_pct != null || q.total > 0)).map((s) => (
                    <tr key={s.year} className="border-b border-[#f1f5f9]">
                      <td className="px-3 py-1.5 font-bold text-[#0f172a] dark:text-gray-200">{s.year}</td>
                      {["q1", "q2", "q3", "q4"].map((k) => {
                        const q = s.quarters?.[k];
                        if (!q) return <td key={k} className="px-2 py-1.5 text-right text-slate-300">—</td>;
                        if (q.open) return <td key={k} className="px-2 py-1.5 text-right text-amber-500"><span className="italic text-xs font-semibold">open</span></td>;
                        const p = q.yoy_pct;
                        return <td key={k} className={`px-2 py-1.5 text-right tabular-nums font-medium ${p == null ? "text-slate-300" : p >= 0 ? "text-emerald-600" : "text-red-600"}`}>{p == null ? "—" : `${p > 0 ? "+" : ""}${p}%`}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cargo Statistics */}
      {cargoData && (
        <Card className="border-[#e2e8f0] mb-8" data-testid="cargo-stats-card">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <Package size={20} className="text-[#d4af37]" />
              Cargo Statistics
            </CardTitle>
            <p className="text-sm text-[#64748b] mt-1">Cargo flights only · {periodLabel}</p>
          </CardHeader>
          <CardContent>
            {cargoData.total_flights === 0 ? (
              <p className="text-sm text-[#94a3b8] py-6 text-center" data-testid="cargo-empty">No cargo flights for this period.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40">
                    <div className="w-12 h-12 bg-[#0f172a] rounded-lg flex items-center justify-center"><Package size={24} className="text-[#d4af37]" /></div>
                    <div>
                      <p className="text-sm text-[#64748b]">Cargo Flights</p>
                      <p className="text-2xl font-bold text-[#0f172a] dark:text-white font-['Outfit']" data-testid="cargo-total-flights">{cargoData.total_flights.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40">
                    <div className="w-12 h-12 bg-[#d4af37] rounded-lg flex items-center justify-center"><Scale size={24} className="text-[#0f172a]" /></div>
                    <div>
                      <p className="text-sm text-[#64748b]">Total Cargo Weight</p>
                      <p className="text-2xl font-bold text-[#0f172a] dark:text-white font-['Outfit']" data-testid="cargo-total-kg">{cargoData.total_kg.toLocaleString()} kg</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40">
                    <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center"><Scale size={24} className="text-white" /></div>
                    <div>
                      <p className="text-sm text-[#64748b]">Avg per Flight</p>
                      <p className="text-2xl font-bold text-[#0f172a] dark:text-white font-['Outfit']" data-testid="cargo-avg-kg">{Math.round(cargoData.total_kg / cargoData.total_flights).toLocaleString()} kg</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Monthly cargo weight */}
                  <div>
                    <p className="text-sm font-semibold text-[#0f172a] dark:text-gray-200 mb-2">Cargo Weight by Month (kg)</p>
                    <div className="h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={cargoData.by_month} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                          <XAxis dataKey="month_name" tick={{ fill: axisColor, fontSize: 11 }} />
                          <YAxis tick={{ fill: axisColor, fontSize: 11 }} />
                          <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: labelColor }} itemStyle={{ color: labelColor }} />
                          <Legend />
                          <Bar dataKey="kg" name="Cargo (kg)" fill={COLORS.gold} radius={[4, 4, 0, 0]} />
                          <Bar dataKey="flights" name="Flights" fill={chartNavy} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  {/* Top cargo operators */}
                  <div>
                    <p className="text-sm font-semibold text-[#0f172a] dark:text-gray-200 mb-2">Top Cargo Operators</p>
                    <div className="space-y-2" data-testid="cargo-operators">
                      {cargoData.top_operators.map((op, i) => (
                        <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/40 text-sm">
                          <span className="font-medium text-[#0f172a] dark:text-gray-200 truncate mr-2">{op.operator}</span>
                          <span className="flex items-center gap-3 flex-shrink-0 text-[#64748b] dark:text-gray-400">
                            <span>{op.flights} flt</span>
                            <span className="font-semibold text-[#d4af37]">{op.kg.toLocaleString()} kg</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delay Statistics */}
      {delayData && (
        <Card className="border-[#e2e8f0] mb-8" data-testid="delay-stats-card">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <Clock size={20} className="text-[#d4af37]" />
              Delay Statistics (Scheduled vs Actual)
            </CardTitle>
            <p className="text-sm text-[#64748b] mt-1">Arrivals use ATA, departures use ATD · {periodLabel}</p>
          </CardHeader>
          <CardContent>
            {delayData.total === 0 ? (
              <p className="text-sm text-[#94a3b8] py-6 text-center" data-testid="delay-empty">No actual-time data for this period.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40">
                    <p className="text-sm text-[#64748b]">Movements analysed</p>
                    <p className="text-2xl font-bold text-[#0f172a] dark:text-white font-['Outfit']" data-testid="delay-total">{delayData.total.toLocaleString()}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20">
                    <p className="text-sm text-[#64748b]">On-time (&lt; 15 min)</p>
                    <p className="text-2xl font-bold text-emerald-600 font-['Outfit']" data-testid="delay-otp">{delayData.on_time_pct}%</p>
                  </div>
                  <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20">
                    <p className="text-sm text-[#64748b]">Avg delay (late only)</p>
                    <p className="text-2xl font-bold text-amber-600 font-['Outfit']" data-testid="delay-avg">{delayData.avg_late_min} min</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40">
                    <p className="text-sm text-[#64748b]">Late movements</p>
                    <p className="text-2xl font-bold text-[#0f172a] dark:text-white font-['Outfit']" data-testid="delay-late">{delayData.late_count.toLocaleString()}</p>
                  </div>
                </div>

                <p className="text-sm font-semibold text-[#0f172a] dark:text-gray-200 mb-2">Delay Distribution (stacked: Arrivals + Departures = combined)</p>
                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={delayData.data} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                      <XAxis dataKey="bucket" tick={{ fill: axisColor, fontSize: 11 }} />
                      <YAxis tick={{ fill: axisColor, fontSize: 11 }} />
                      <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: labelColor }} itemStyle={{ color: labelColor }} />
                      <Legend />
                      <Bar dataKey="arrivals" name="Arrivals" stackId="d" fill={chartNavy} radius={[0, 0, 0, 0]} />
                      <Bar dataKey="departures" name="Departures" stackId="d" fill={COLORS.gold} radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="combined" position="top" style={{ fill: axisColor, fontSize: 11 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-[#94a3b8] mt-3">Delay = Actual − Scheduled. "Early" = arrived/departed before schedule. Times are treated within the day (post-23:00 derogation flights are rare, so midnight wrap is not applied).</p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* 500km Proximity Radius */}
      {proximityData && (
        <Card className="border-[#e2e8f0] mb-8" data-testid="proximity-card">
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="font-['Outfit'] text-[#0f172a] flex items-center gap-2">
                  <Radar size={20} className="text-[#d4af37]" />
                  Flights within {proximityData.radius_km} km of EBCI
                </CardTitle>
                <p className="text-sm text-[#64748b] mt-1">
                  Short-haul catchment — arrivals from and departures to airports inside a {proximityData.radius_km} km radius.
                </p>
              </div>
              <div className="flex items-center gap-2" data-testid="radius-control">
                <label className="text-xs text-[#64748b] whitespace-nowrap">Radius (km)</label>
                <input
                  type="number"
                  min="50"
                  max="20000"
                  step="50"
                  value={radiusInput}
                  onChange={(e) => setRadiusInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") applyRadius(); }}
                  className="w-24 h-9 px-2 rounded-md border border-[#e2e8f0] bg-white dark:bg-[#0f172a] dark:border-[#334155] text-sm text-[#0f172a] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
                  data-testid="radius-input"
                />
                <button
                  onClick={applyRadius}
                  className="h-9 px-3 rounded-md bg-[#d4af37] text-[#0f172a] text-sm font-semibold hover:bg-[#c19f2f] transition-colors"
                  data-testid="radius-apply-btn"
                >
                  Apply
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Stats column */}
              <div className="lg:col-span-2 space-y-4" data-testid="proximity-stats">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-[#0f172a] text-white">
                    <p className="text-xs text-[#94a3b8]">Within {proximityData.radius_km} km</p>
                    <p className="text-3xl font-bold font-['Outfit'] text-[#d4af37]" data-testid="proximity-within-count">{proximityData.within_flights}</p>
                    <p className="text-xs text-[#94a3b8]">
                      flights · {proximityData.total ? Math.round((proximityData.within_flights / proximityData.total) * 100) : 0}% of all
                    </p>
                    <p className="text-lg font-bold font-['Outfit'] text-[#d4af37] mt-2" data-testid="proximity-within-pax">{proximityData.within_pax.toLocaleString()} pax</p>
                    {proximityData.local_flights > 0 && (
                      <p className="text-[11px] text-[#94a3b8] mt-1">incl. {proximityData.local_flights} local Belgian (EBCI / ZZZZ / EB·)</p>
                    )}
                  </div>
                  <div className="p-4 rounded-xl bg-[#f1f5f9]">
                    <p className="text-xs text-[#64748b]">Beyond radius</p>
                    <p className="text-3xl font-bold font-['Outfit'] text-[#0f172a]">{proximityData.outside_flights}</p>
                    <p className="text-xs text-[#64748b]">flights · {proximityData.within_airports_count} airports inside</p>
                    <p className="text-lg font-bold font-['Outfit'] text-[#0f172a] mt-2">{proximityData.outside_pax.toLocaleString()} pax</p>
                  </div>
                </div>

                {/* Distance buckets */}
                <div className="space-y-3 pt-2">
                  {proximityData.buckets.map((b, i) => {
                    const max = Math.max(...proximityData.buckets.map((x) => x.count), 1);
                    const bucketTotal = proximityData.buckets.reduce((s, x) => s + x.count, 0) || 1;
                    const pct = Math.round((b.count / bucketTotal) * 100);
                    const isOut = b.range.startsWith(">");
                    return (
                      <div key={b.range} className="flex items-center gap-3">
                        <span className="text-xs text-[#64748b] w-24 shrink-0">{b.range}</span>
                        <div className="flex-1 h-3 bg-[#f1f5f9] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${(b.count / max) * 100}%`, backgroundColor: isOut ? COLORS.slate : pieColors[i % PIE_COLORS.length] }}
                          />
                        </div>
                        <span className="text-xs font-medium text-[#0f172a] w-16 text-right">{b.count} ({pct}%)</span>
                      </div>
                    );
                  })}
                  {proximityData.unknown_flights > 0 && (
                    <p className="text-xs text-[#94a3b8] pt-1" data-testid="proximity-unmapped-note">
                      {proximityData.unknown_flights} flight(s) excluded — non-standard codes:{" "}
                      {(proximityData.unmapped_codes || []).map((c) => `${c.code} (${c.count})`).join(", ")}
                    </p>
                  )}
                </div>
              </div>

              {/* Map column */}
              <div className="lg:col-span-3 h-[380px] rounded-xl overflow-hidden border border-[#e2e8f0]" data-testid="proximity-map">
                <MapContainer center={EBCI_CENTER} zoom={5} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Circle
                    center={EBCI_CENTER}
                    radius={proximityData.radius_km * 1000}
                    pathOptions={{ color: "#d4af37", fillColor: "#d4af37", fillOpacity: 0.08, weight: 2 }}
                  />
                  <CircleMarker center={EBCI_CENTER} radius={7} pathOptions={{ color: "#0f172a", fillColor: "#0f172a", fillOpacity: 1 }}>
                    <LeafletTooltip>EBCI — Brussels South Charleroi</LeafletTooltip>
                  </CircleMarker>
                  {proximityData.airports.map((a) => {
                    const maxCount = Math.max(...proximityData.airports.map((x) => x.count), 1);
                    const r = 4 + (a.count / maxCount) * 14;
                    return (
                      <CircleMarker
                        key={a.icao}
                        center={[a.lat, a.lon]}
                        radius={r}
                        pathOptions={{ color: "#3b82f6", fillColor: "#3b82f6", fillOpacity: 0.6, weight: 1 }}
                      >
                        <LeafletTooltip>
                          <span className="font-semibold">{a.icao}</span> — {a.name}<br />
                          {a.count} flights · {a.pax} pax · {a.distance_km} km
                        </LeafletTooltip>
                      </CircleMarker>
                    );
                  })}
                </MapContainer>
              </div>
            </div>
          </CardContent>
        </Card>
      )}


      {/* Two Column Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Flight Types Pie Chart */}
        <Card className="border-[#e2e8f0]">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <PlaneTakeoff size={20} />
              Flight Types Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {flightTypeData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-[#64748b]">
                No data available
              </div>
            ) : (
              <div className="h-[340px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={flightTypeData}
                      cx="50%"
                      cy="45%"
                      labelLine={false}
                      label={makePieLabel(labelColor)}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="type"
                    >
                      {flightTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={pieColors[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: labelColor }} itemStyle={{ color: labelColor }} formatter={(value, name) => [`${value} flights`, name]} />
                    <Legend layout="horizontal" verticalAlign="bottom" align="center" iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} formatter={(value, entry) => `${value} ${Math.round(((entry?.payload?.count || 0) / flightTypeTotal) * 100)}%`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Aircraft Types Chart */}
        <Card className="border-[#e2e8f0]">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <Building2 size={20} />
              Aircraft Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            {aircraftData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-[#64748b]">
                No data available
              </div>
            ) : (
              <div className="h-[420px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={aircraftPct} layout="vertical" margin={{ top: 5, right: 70, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                    <XAxis type="number" tick={{ fill: axisColor, fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="aircraft"
                      tick={(props) => {
                        const { x, y, payload } = props;
                        const label = String(payload.value || "").length > 20
                          ? String(payload.value).slice(0, 19) + "…"
                          : payload.value;
                        return (
                          <text x={x} y={y} dy={4} textAnchor="end" fill={labelColor} fontSize={11}>{label}</text>
                        );
                      }}
                      width={160}
                      interval={0}
                    />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: labelColor }} itemStyle={{ color: labelColor }} formatter={(value, n, p) => [`${value} flights (${p.payload._pct.toFixed(0)}%)`, "Count"]} />
                    <Bar dataKey="count" fill={chartNavy} radius={[0, 4, 4, 0]} barSize={16}>
                      <LabelList dataKey="_label" position="right" fill={labelColor} fontSize={11} fontWeight={600} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Operators and Routes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Operators */}
        <Card className="border-[#e2e8f0]">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <Building2 size={20} />
              Top Operators
            </CardTitle>
          </CardHeader>
          <CardContent>
            {operatorData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-[#64748b]">
                No data available
              </div>
            ) : (
              <div className="space-y-4" data-testid="top-operators-list">
                {operatorData.slice(0, 15).map((op, index) => (
                  <div key={op.operator} className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-[#0f172a] rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-[#0f172a]">{op.operator}</span>
                        <span className="text-sm text-[#64748b]">{op.flights} flights · {Math.round((op.flights / operatorTotal) * 100)}%</span>
                      </div>
                      <div className="h-2 bg-[#f1f5f9] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#d4af37] rounded-full transition-all duration-500"
                          style={{ width: `${(op.flights / (operatorData[0]?.flights || 1)) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Routes */}
        <Card className="border-[#e2e8f0]">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <Route size={20} />
              Top Routes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {routesData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-[#64748b]">
                No data available
              </div>
            ) : (
              <div className="space-y-4" data-testid="top-routes-list">
                {routesData.slice(0, 15).map((route, index) => (
                  <div key={route.route} className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-[#d4af37] rounded-lg flex items-center justify-center text-[#0f172a] font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-mono font-medium text-[#0f172a]">{route.route}</span>
                        <span className="text-sm text-[#64748b]">{route.count} flights · {Math.round((route.count / routeTotal) * 100)}%</span>
                      </div>
                      <div className="h-2 bg-[#f1f5f9] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#0f172a] rounded-full transition-all duration-500"
                          style={{ width: `${(route.count / (routesData[0]?.count || 1)) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

        {/* Services Statistics */}
        <div className="grid grid-cols-1 gap-8">
        <Card className="border-[#e2e8f0]">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-[#0f172a] flex items-center gap-2">
              <Fuel size={20} />
              Requested Services
            </CardTitle>
            <p className="text-sm text-[#64748b]">All services ranked by number of flights. Values shown at the end of each bar.</p>
          </CardHeader>
          <CardContent>
            {servicePct.length === 0 ? (
              <div className="h-[200px] flex items-center justify-center text-[#64748b]">
                No data available
              </div>
            ) : (
              <div style={{ height: `${Math.max(300, servicePct.length * 34)}px` }} data-testid="requested-services-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={servicePct} layout="vertical" margin={{ top: 5, right: 72, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                    <XAxis type="number" tick={{ fill: axisColor, fontSize: 12 }} allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="service"
                      tick={{ fill: labelColor, fontSize: 12 }}
                      width={130}
                      interval={0}
                    />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: labelColor }} itemStyle={{ color: labelColor }} formatter={(value, n, p) => [`${value} flights (${p.payload._pct.toFixed(0)}%)`, "Count"]} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={18}>
                      {servicePct.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={pieColors[index % PIE_COLORS.length]} />
                      ))}
                      <LabelList dataKey="_label" position="right" fill={labelColor} fontSize={12} fontWeight={600} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Flights by Continent Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {/* By Continent - Bar */}
        <Card className="bg-white dark:bg-[#1e293b]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a] dark:text-white">
              <Globe className="text-[#d4af37]" size={20} />
              Flights by Continent
            </CardTitle>
          </CardHeader>
          <CardContent>
            {continentData.length === 0 ? (
              <div className="text-center py-8 text-[#64748b]">No data available</div>
            ) : (
              <div className="h-[320px]" data-testid="continent-bar-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={continentPct} layout="vertical" margin={{ top: 5, right: 72, left: 60, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis type="number" tick={{ fill: axisColor, fontSize: 12 }} />
                    <YAxis dataKey="continent" type="category" tick={{ fill: axisColor, fontSize: 11 }} width={140} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: labelColor }} itemStyle={{ color: labelColor }} formatter={(value, n, p) => [`${value} flights (${p.payload._pct.toFixed(0)}%)`, "Count"]} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {continentPct.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={continentColors[entry.continent] || pieColors[index % PIE_COLORS.length]} />
                      ))}
                      <LabelList dataKey="_label" position="right" fill={labelColor} fontSize={11} fontWeight={600} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* By Continent - Pie */}
        <Card className="bg-white dark:bg-[#1e293b]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a] dark:text-white">
              <Globe className="text-[#d4af37]" size={20} />
              Continent Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {continentData.length === 0 ? (
              <div className="text-center py-8 text-[#64748b]">No data available</div>
            ) : (
              <div className="h-[320px]" data-testid="continent-pie-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={continentData}
                      dataKey="count"
                      nameKey="continent"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      labelLine={false}
                      label={makePieLabel(labelColor)}
                    >
                      {continentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={continentColors[entry.continent] || pieColors[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: labelColor }} itemStyle={{ color: labelColor }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* World Map — all destinations */}
        <Card className="bg-white dark:bg-[#1e293b] md:col-span-2" data-testid="geo-map-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a] dark:text-white">
              <Globe className="text-[#d4af37]" size={20} />
              Destination Map (worldwide)
            </CardTitle>
            <p className="text-sm text-[#64748b] mt-1">
              Every airport connected to EBCI. Marker size ∝ number of flights, colored by continent.
            </p>
            {geoData && geoData.airports.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="geo-totals">
                <span className="px-3 py-1 rounded-full bg-[#0f172a] text-white text-xs font-medium">
                  {geoData.airports.length} airports
                </span>
                <span className="px-3 py-1 rounded-full bg-[#f1f5f9] text-[#0f172a] text-xs font-medium">
                  {(geoData.total_flights ?? geoData.airports.reduce((s, a) => s + a.count, 0)).toLocaleString()} flights
                </span>
                <span className="px-3 py-1 rounded-full bg-[#d4af37] text-[#0f172a] text-xs font-medium">
                  {(geoData.total_pax ?? geoData.airports.reduce((s, a) => s + (a.pax || 0), 0)).toLocaleString()} pax
                </span>
                {geoData.unmapped_flights > 0 && (
                  <span className="text-[11px] text-[#94a3b8]">
                    ({geoData.unmapped_flights} not mapped: {(geoData.unmapped_codes || []).map((c) => `${c.code}×${c.count}`).join(", ")})
                  </span>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!geoData || geoData.airports.length === 0 ? (
              <div className="text-center py-8 text-[#64748b]">No data available</div>
            ) : (
              <>
                <div className="h-[460px] rounded-xl overflow-hidden border border-[#e2e8f0]" data-testid="geo-map">
                  <MapContainer center={[42, 8]} zoom={3} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false} worldCopyJump={true}>
                    <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <CircleMarker center={EBCI_CENTER} radius={7} pathOptions={{ color: "#111827", fillColor: "#d4af37", fillOpacity: 1, weight: 2 }}>
                      <LeafletTooltip permanent direction="top">EBCI</LeafletTooltip>
                    </CircleMarker>
                    {geoData.airports.map((a) => {
                      const maxCount = Math.max(...geoData.airports.map((x) => x.count), 1);
                      const r = 4 + (a.count / maxCount) * 16;
                      const color = CONTINENT_COLORS[a.continent] || "#64748b";
                      return (
                        <CircleMarker
                          key={a.icao}
                          center={[a.lat, a.lon]}
                          radius={r}
                          pathOptions={{ color: "#334155", fillColor: color, fillOpacity: 0.75, weight: 1 }}
                        >
                          <LeafletTooltip>
                            <span className="font-semibold">{a.icao}</span> — {a.name}<br />
                            {a.count} flights · {a.pax} pax · {a.continent}
                          </LeafletTooltip>
                        </CircleMarker>
                      );
                    })}
                  </MapContainer>
                </div>
                {/* Continent legend */}
                <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 justify-center">
                  {Object.entries(CONTINENT_COLORS).map(([name, color]) => (
                    <span key={name} className="flex items-center gap-2 text-xs text-[#64748b]">
                      <span className="w-3 h-3 rounded-full inline-block border border-[#94a3b8]/50" style={{ backgroundColor: color }} />
                      {name}
                    </span>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Apron Usage Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {/* Usage by Apron */}
        <Card className="bg-white dark:bg-[#1e293b]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="text-[#d4af37]" size={20} />
              Usage by Apron
            </CardTitle>
          </CardHeader>
          <CardContent>
            {apronUsageData.by_apron.length === 0 ? (
              <div className="text-center py-8 text-[#64748b]">No data available</div>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={apronUsageData.by_apron}
                      dataKey="count"
                      nameKey="apron"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ apron, count, percent }) => `${apron}: ${count} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {apronUsageData.by_apron.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={pieColors[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: labelColor }} itemStyle={{ color: labelColor }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage by Position */}
        <Card className="bg-white dark:bg-[#1e293b]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="text-[#d4af37]" size={20} />
              Top Parking Positions
            </CardTitle>
            <p className="text-sm text-[#64748b]">Every parking stand that was used at least once, with share of total.</p>
          </CardHeader>
          <CardContent>
            {apronUsageData.by_position.length === 0 ? (
              <div className="text-center py-8 text-[#64748b]">No data available</div>
            ) : (
              <div style={{ height: `${Math.max(300, positionPct.length * 30)}px` }} data-testid="parking-positions-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={positionPct} layout="vertical" margin={{ top: 5, right: 72, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                    <XAxis type="number" tick={{ fill: axisColor, fontSize: 12 }} allowDecimals={false} />
                    <YAxis dataKey="position" type="category" tick={{ fill: labelColor, fontSize: 11 }} width={50} interval={0} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: labelColor }} itemStyle={{ color: labelColor }} formatter={(value, n, p) => [`${value} flights (${p.payload._pct.toFixed(0)}%)`, "Count"]} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={16}>
                      {positionPct.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={pieColors[index % PIE_COLORS.length]} />
                      ))}
                      <LabelList dataKey="_label" position="right" fill={labelColor} fontSize={11} fontWeight={600} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Aircraft Manufacturers by Apron Usage */}
        <Card className="bg-white dark:bg-[#1e293b] md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plane className="text-[#d4af37]" size={20} />
              Aircraft Manufacturers (Apron Usage)
            </CardTitle>
            <p className="text-sm text-[#64748b]">Grouped by manufacturer (e.g. PC-12 &amp; PC-24 counted under Pilatus).</p>
          </CardHeader>
          <CardContent>
            {aircraftApronData.length === 0 ? (
              <div className="text-center py-8 text-[#64748b]">No data available</div>
            ) : (
              <div style={{ height: `${Math.max(300, aircraftApronData.length * 34)}px` }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={aircraftApronPct} layout="vertical" margin={{ top: 5, right: 72, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                    <XAxis type="number" tick={{ fill: axisColor, fontSize: 12 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="aircraft_type" tick={{ fill: labelColor, fontSize: 12 }} width={170} interval={0} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: labelColor }} itemStyle={{ color: labelColor }} formatter={(value, n, p) => [`${value} flights (${p.payload._pct.toFixed(0)}%)`, "Count"]} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={18}>
                      {aircraftApronPct.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={pieColors[index % PIE_COLORS.length]} />
                      ))}
                      <LabelList dataKey="_label" position="right" fill={labelColor} fontSize={12} fontWeight={600} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
